import axios from 'axios';
import config from '../../config/config';
import { parseTaiwanTime } from '../../utils/dateTimeUtils';

// DrEnabled 資料表介面
export interface IDrEnabled {
    uid: number;
    Class: string;
    No: number;
    Name: string;
    BeginDate: string;
    EndDate: string;
}

// 根據名稱查詢 DrEnabled 設定
// Get DrEnabled config by name
// Get /api/DrTable/enabled/name/:name
export const getDrEnabledByName = async (name: string, classYear?: string): Promise<IDrEnabled | null> => {
    console.log('=== DrEnabled 資料庫查詢 ===');
    console.log('查詢名稱:', name);
    console.log('年度參數:', classYear);
    
    // 實際從資料庫 API 查詢 DrEnabled 表格
    const url = config.server.url + `/api/DrTable/enabled/name/${encodeURIComponent(name)}`;
    
    console.log('📡 API 請求 URL:', url);
    console.log('📋 SQL 相當於: SELECT * FROM DrEnabled WHERE Name = ? AND Class = ?');
    
    return new Promise((resolve, reject) => {
        axios
            .get(url, {
                params: classYear ? { class: classYear } : {}
            })
            .then((response) => {
                console.log('=== 資料庫查詢成功 ===');
                console.log('HTTP 狀態碼:', response.status);
                console.log('📊 資料庫回傳資料:', response.data);
                
                if (response.data && response.data.uid) {
                    console.log('✅ 找到 DrEnabled 記錄:');
                    console.log('  uid:', response.data.uid);
                    console.log('  Class:', response.data.Class);
                    console.log('  Name:', response.data.Name);
                    console.log('  BeginDate:', response.data.BeginDate);
                    console.log('  EndDate:', response.data.EndDate);
                    resolve(response.data);
                } else {
                    console.log('❌ 資料庫中未找到對應記錄');
                    resolve(null);
                }
            })
            .catch((error) => {
                console.log('=== 資料庫查詢失敗 ===');
                console.error('錯誤詳情:', error);
                
                if (error.response) {
                    console.error('HTTP 狀態碼:', error.response.status);
                    console.error('錯誤回應:', error.response.data);
                    
                    if (error.response.status === 404) {
                        console.log('404: API 端點不存在或記錄未找到');
                        resolve(null);
                    } else {
                        console.log('其他 HTTP 錯誤，使用 null');
                        resolve(null);
                    }
                } else if (error.request) {
                    console.error('網路連接失敗');
                    resolve(null);
                } else {
                    console.error('其他錯誤:', error.message);
                    resolve(null);
                }
            });
    });
};

// 前端時間控制設定（與資料庫 DrEnabled 表格一致）
const FEATURE_TIME_CONFIG = {
    '報名審核通過的小隊': {
        beginDate: '2026-03-04 00:00:00', // 與資料庫一致：明天開始
        endDate: '2027-03-03 00:00:00',   // 與資料庫一致
        enabled: true // 是否啟用這個功能
    },
    '決賽名單': {
        beginDate: '2026-04-01 00:00:00', // 4月1日開始
        endDate: '2027-03-03 23:59:59',   // 明年結束
        enabled: true
    },
    '決賽獲獎隊伍': {
        beginDate: '2026-05-01 00:00:00', // 5月1日開始
        endDate: '2027-03-03 23:59:59',   // 明年結束
        enabled: true
    }
    // 可以在這裡添加更多功能的時間控制
};

// 測試用：暫時控制各功能顯示
const TEST_MODE = {
    '報名審核通過的小隊': null,  // 設為 true 強制開啟，false 強制關閉，null 使用正常時間檢查
    '決賽名單': null,           // null = 使用正常時間檢查
    '決賽獲獎隊伍': null        // null = 使用正常時間檢查
};

// 檢查指定功能是否在開啟時間內
export const checkFeatureEnabled = async (featureName: string, classYear?: string): Promise<boolean> => {
    try {
        console.log('=== 資料庫時間控制檢查 ===');
        console.log('功能名稱:', featureName);
        console.log('年度:', classYear);
        
        // 檢查測試模式
        const testMode = TEST_MODE[featureName as keyof typeof TEST_MODE];
        if (testMode !== null && testMode !== undefined) {
            console.log(`🧪 測試模式: ${testMode ? '強制開啟' : '強制關閉'}`);
            return testMode;
        }
        
        // 嘗試從資料庫獲取設定
        console.log('📡 嘗試從資料庫獲取時間設定...');
        let dbConfig = await getDrEnabledByName(featureName, classYear);
        
        // 如果直接查詢失敗，嘗試從所有記錄中查找
        if (!dbConfig) {
            console.log('🔄 直接查詢失敗，嘗試從所有記錄中查找...');
            dbConfig = await getDrEnabledByNameFromAll(featureName, classYear);
        }
        
        let beginDate: Date;
        let endDate: Date;
        let configSource = '';
        
        if (dbConfig) {
            console.log('✅ 從資料庫獲取到設定:', dbConfig);
            // 使用台灣時區解析時間
            beginDate = parseTaiwanTime(dbConfig.BeginDate);
            endDate = parseTaiwanTime(dbConfig.EndDate);
            configSource = '資料庫';
        } else {
            console.log('❌ 資料庫中未找到設定，使用前端預設設定');
            // 檢查是否有前端設定
            const featureConfig = FEATURE_TIME_CONFIG[featureName as keyof typeof FEATURE_TIME_CONFIG];
            
            if (!featureConfig) {
                console.log(`功能 "${featureName}" 未找到任何設定，預設關閉`);
                return false;
            }
            
            if (!featureConfig.enabled) {
                console.log(`功能 "${featureName}" 已被停用`);
                return false;
            }
            
            beginDate = new Date(featureConfig.beginDate);
            endDate = new Date(featureConfig.endDate);
            configSource = '前端預設';
        }

        const now = new Date();

        console.log('=== 詳細時間檢查 ===');
        console.log('功能名稱:', featureName);
        console.log('設定來源:', configSource);
        if (dbConfig) {
            console.log('資料庫原始設定:', dbConfig);
        }
        console.log('');
        
        console.log('📅 時間解析:');
        if (dbConfig) {
            console.log('  資料庫開始時間字串:', dbConfig.BeginDate);
            console.log('  資料庫結束時間字串:', dbConfig.EndDate);
        }
        console.log('  解析後開始時間:', beginDate);
        console.log('  解析後結束時間:', endDate);
        console.log('');
        
        console.log('🕐 當前時間:');
        console.log('  當前時間物件:', now);
        console.log('  當前時間 ISO:', now.toISOString());
        console.log('  當前時間本地:', now.toLocaleString());
        console.log('  當前時間戳:', now.getTime());
        console.log('');
        
        console.log('⏰ 開始時間:');
        console.log('  開始時間 ISO:', beginDate.toISOString());
        console.log('  開始時間本地:', beginDate.toLocaleString());
        console.log('  開始時間戳:', beginDate.getTime());
        console.log('');
        
        console.log('⏰ 結束時間:');
        console.log('  結束時間 ISO:', endDate.toISOString());
        console.log('  結束時間本地:', endDate.toLocaleString());
        console.log('  結束時間戳:', endDate.getTime());
        console.log('');
        
        console.log('🔍 時間比較:');
        console.log('  當前 >= 開始?', now >= beginDate, `(${now.getTime()} >= ${beginDate.getTime()})`);
        console.log('  當前 <= 結束?', now <= endDate, `(${now.getTime()} <= ${endDate.getTime()})`);
        console.log('  時間差（分鐘）:');
        console.log('    距離開始:', Math.round((beginDate.getTime() - now.getTime()) / (1000 * 60)), '分鐘');
        console.log('    距離結束:', Math.round((endDate.getTime() - now.getTime()) / (1000 * 60)), '分鐘');
        console.log('');
        
        const isInRange = now >= beginDate && now <= endDate;
        console.log('🎯 最終結果:', isInRange ? '✅ 在時間範圍內' : '❌ 不在時間範圍內');
        console.log('===============================');

        const isEnabled = now >= beginDate && now <= endDate;
        
        if (isEnabled) {
            console.log('✅ 功能已開啟');
        } else {
            console.log('❌ 功能未開啟（不在時間範圍內）');
        }
        
        return isEnabled;
    } catch (error) {
        console.error(`檢查功能 "${featureName}" 開啟狀態失敗:`, error);
        return false; // 發生錯誤時預設關閉
    }
};

// 獲取功能的時間信息（除錯用）
export const getFeatureTimeInfo = async (featureName: string, classYear?: string): Promise<{
    currentTime: string;
    beginDate: string;
    endDate: string;
    isEnabled: boolean;
    source: string;
    rawBeginDate?: string;
    rawEndDate?: string;
} | null> => {
    try {
        const now = new Date();
        
        // 嘗試從資料庫獲取設定
        let dbConfig = await getDrEnabledByName(featureName, classYear);
        
        if (!dbConfig) {
            dbConfig = await getDrEnabledByNameFromAll(featureName, classYear);
        }
        
        let beginDate: Date;
        let endDate: Date;
        let configSource = '';
        let rawBeginDate = '';
        let rawEndDate = '';
        
        if (dbConfig) {
            rawBeginDate = dbConfig.BeginDate;
            rawEndDate = dbConfig.EndDate;
            
            console.log('📅 原始資料庫時間字串:');
            console.log('  BeginDate:', dbConfig.BeginDate, '(類型:', typeof dbConfig.BeginDate, ')');
            console.log('  EndDate:', dbConfig.EndDate, '(類型:', typeof dbConfig.EndDate, ')');
            
            // 使用台灣時區解析時間
            beginDate = parseTaiwanTime(dbConfig.BeginDate);
            endDate = parseTaiwanTime(dbConfig.EndDate);
            
            console.log('📅 解析後的時間:');
            console.log('  beginDate:', beginDate.toISOString(), '本地:', beginDate.toLocaleString('zh-TW', { timeZone: 'Asia/Taipei' }));
            console.log('  endDate:', endDate.toISOString(), '本地:', endDate.toLocaleString('zh-TW', { timeZone: 'Asia/Taipei' }));
            
            configSource = '資料庫';
        } else {
            const featureConfig = FEATURE_TIME_CONFIG[featureName as keyof typeof FEATURE_TIME_CONFIG];
            
            if (!featureConfig) {
                return null;
            }
            
            rawBeginDate = featureConfig.beginDate;
            rawEndDate = featureConfig.endDate;
            beginDate = new Date(featureConfig.beginDate);
            endDate = new Date(featureConfig.endDate);
            configSource = '前端預設';
        }
        
        const isEnabled = now >= beginDate && now <= endDate;
        
        return {
            currentTime: now.toLocaleString('zh-TW', { timeZone: 'Asia/Taipei' }),
            beginDate: beginDate.toLocaleString('zh-TW', { timeZone: 'Asia/Taipei' }),
            endDate: endDate.toLocaleString('zh-TW', { timeZone: 'Asia/Taipei' }),
            isEnabled,
            source: configSource,
            rawBeginDate,
            rawEndDate
        };
    } catch (error) {
        console.error(`獲取功能時間信息失敗:`, error);
        return null;
    }
};

// 取得所有 DrEnabled 設定（管理用）
export const getAllDrEnabled = async (classYear?: string): Promise<IDrEnabled[]> => {
    console.log('=== 查詢所有 DrEnabled 記錄 ===');
    console.log('📋 SQL 相當於: SELECT * FROM DrEnabled');
    
    // 由於遠端 API 可能沒有 DrEnabled 端點，先嘗試，失敗則使用備用方案
    const url = config.server.url + '/api/DrTable/enabled';
    
    return new Promise((resolve, reject) => {
        axios
            .get(url, {
                params: classYear ? { class: classYear } : {}
            })
            .then((response) => {
                console.log('✅ 成功獲取所有 DrEnabled 記錄:', response.data);
                resolve(response.data || []);
            })
            .catch((error) => {
                console.log('❌ API 端點不存在，使用備用資料');
                
                // 備用方案：使用你提供的資料庫截圖資料
                const backupData: IDrEnabled[] = [
                    {
                        uid: 27,
                        Class: '115年度',
                        No: 15,
                        Name: '報名審核通過的小隊',
                        BeginDate: '2026-03-04 00:00:00',
                        EndDate: '2027-03-03 00:00:00'
                    },
                    {
                        uid: 26,
                        Class: '115年度',
                        No: 15,
                        Name: '新開放項目',
                        BeginDate: '2026-03-04 00:00:00',
                        EndDate: '2027-03-03 00:00:00'
                    }
                ];
                
                console.log('📊 使用備用資料:', backupData);
                resolve(backupData);
            });
    });
};

// 從所有記錄中查找特定名稱的設定
export const getDrEnabledByNameFromAll = async (name: string, classYear?: string): Promise<IDrEnabled | null> => {
    try {
        const allRecords = await getAllDrEnabled(classYear);
        console.log('🔍 在所有記錄中查找:', name);
        console.log('📊 可用記錄:', allRecords.map(r => r.Name));
        
        const found = allRecords.find(record => 
            record.Name === name && 
            (!classYear || record.Class === classYear)
        );
        
        if (found) {
            console.log('✅ 找到匹配記錄:', found);
        } else {
            console.log('❌ 未找到匹配記錄');
        }
        
        return found || null;
    } catch (error) {
        console.error('查詢失敗:', error);
        return null;
    }
};
